import React, {PureComponent, Fragment, MouseEvent} from 'react'
import { SerializedStyles } from '@emotion/core'

import { StoreState } from '@store'
import { ABConfigsStore, ABQuotesFlags } from '@store/ab-configs'

import Analytics from '@lib/Analytics'
import ApiClient, { LeadData } from '@lib/ApiClient'
import Config from '@lib/Config'
import Pixels from '@lib/Pixels'
import Log from '@lib/Log'
import { getPlanByAge } from '@lib/PlansFilter'
import { getUrlSearchParam } from '@lib/UrlParams'
import { saveIncomingProspects } from '@lib/saveIncomingProspects'
import {
  getExternalAb,
  getIndustryType,
  getLeadProductType,
} from '@lib/program-mapping'

import { PublicHeader } from '@components/PublicHeader'
import { ProductSelectPopup } from '@components/ProductSelectPopup'
import { LeadgenPopupFlow, LeadgenFormData, LeadgenPopupType } from '@components/LeadgenPopupFlow'

import { PreLeadPopup } from './PreLeadPopup'
import { fillLeadgenQuestionsData } from './utils/leadgen-question-data'
import { compileAnalyticsEventProps } from '@lib/analytics-event-props'

import headerNavData from './data/header-nav.json'
import productsData from './data/products.json'
import ScheduleLeadgenData from './data/schedule-leadgen-data.json'
import MSLeadgenData from './data/ms-leadgen-data.json'
import MALeadgenData from './data/ma-leadgen-data.json'
import LILeadgenData from './data/li-leadgen-data.json'
import GILeadgenData from './data/gi-leadgen-data.json'
import DILeadgenData from './data/di-leadgen-data.json'
import AILeadgenData from './data/ai-leadgen-data.json'
import {getPhoneNumber} from "@lib/PhoneNumber";

const { SUNFIRE_WEB_URL } = process.env


fillLeadgenQuestionsData(MSLeadgenData)
fillLeadgenQuestionsData(MALeadgenData)
fillLeadgenQuestionsData(LILeadgenData)
fillLeadgenQuestionsData(GILeadgenData)
fillLeadgenQuestionsData(DILeadgenData)
fillLeadgenQuestionsData(AILeadgenData)


type Props = StoreState & {
  phoneMapping: PhoneMapping
  headerStyles?: SerializedStyles
  navItems?: NavGroupType[]
  leadgenPopupType?: LeadgenPopupType
  mode: 'article' | string
  infoBlockActionType?: 'MEDADV' | 'MEDSUP' | 'PDP'
  onSaveSessionData: (key: string, value: any) => void
  hideLogoTagline?: boolean
  showBannerType?: 'INFO' | 'APPT' | 'NONE' | 'LEARN_MORE'
  headerText?: string
  headerButtonText?: string
  useCallNow?: boolean
  showMainGreenButton?: boolean
  useDefaultSchedule?: boolean
}

type State = {
  showSelectProductPopup: boolean
  showLeadgenPopupFor: LeadgenPopupType | false
  hasEnteredZipCode: boolean
  hasEnteredState: boolean
}

export class LeadgenFullHeader extends PureComponent<Props, State> {
  state: State = {
    showSelectProductPopup: false,
    showLeadgenPopupFor: false,
    hasEnteredZipCode: false,
    hasEnteredState: false,
  }

  static defaultProps = {
    showMainGreenButton: true,
    useDefaultSchedule: true,
  }

  constructor(props: Props) {
    super(props)
    this.prefillDefaultStorage()
    this.prefillAnonymousState()
  }

  prefillDefaultStorage = () => {
    const {
      best_time_to_call,
      consultation_date,
      onSaveSessionData,
      useDefaultSchedule,
    } = this.props

    if (!useDefaultSchedule) return

    if (!best_time_to_call) {
      onSaveSessionData('best_time_to_call', 'morning')
    }

    if (!consultation_date) {
      onSaveSessionData('consultation_date', new Date().toString())
    }
  }

  prefillAnonymousState = () => {
    const vParam = getUrlSearchParam('v')
    const quotesParam = getUrlSearchParam('quotes')

    let globalFlag: QuoteABTestType | null = null
    switch (quotesParam) {
      case 'post_lead':
        Analytics.log('Overriding AB quote configs: post_lead', compileAnalyticsEventProps())
        globalFlag = 'post_lead'
        break
      case 'pre_lead':
        Analytics.log('Overriding AB quote configs: pre_lead', compileAnalyticsEventProps())
        globalFlag = 'pre_lead'
        break
      case 'off':
        Analytics.log('Overriding AB quote configs: off', compileAnalyticsEventProps())
        globalFlag = 'off'
        break
    }

    const presetState: ABQuotesFlags = {
      medAdvQuotesFlag: globalFlag,
      medSupQuotesFlag: globalFlag,
      lifeQuotesFlag: globalFlag,
      pdpQuotesFlag: globalFlag
    }

    switch (vParam) {
      case 'ma_anon': {
        Analytics.log('Force Anonymous Shopping on MA flow', compileAnalyticsEventProps())
        presetState.medAdvQuotesFlag = 'pre_lead'
        break
      }
      case 'ms_anon': {
        Analytics.log('Force Anonymous Shopping on MS flow', compileAnalyticsEventProps())
        presetState.medSupQuotesFlag = 'pre_lead'
        break
      }
      case 'li_anon': {
        Analytics.log('Force Anonymous Shopping on LI flow', compileAnalyticsEventProps())
        presetState.lifeQuotesFlag = 'pre_lead'
        break
      }
      case 'pdp_anon': {
        Analytics.log('Force Anonymous Shopping on PDP flow', compileAnalyticsEventProps())
        presetState.pdpQuotesFlag = 'pre_lead'
        break
      }
    }

    ABConfigsStore.presetQuoteConfigs(presetState)
  }

  componentDidMount() {
    Analytics.log('Leadgen Set Mode', compileAnalyticsEventProps())
    Log.info(this, `App loaded in: ${Math.round(performance.now())}`)

    ABConfigsStore.init(this.props.onSaveSessionData)
    ABConfigsStore.fetchConfigs()
  }

  public isShowingSelectedProductPopup = () => this.state.showSelectProductPopup

  /*
   * Handlers:
   */

  handleGreenButtonClick = () => {
    const { useCallNow } = this.props

    if (useCallNow) {
      return this.handleCallNow();
    }

    return this.handleGetMyQuoteTypes();
  }

  handleGetMyQuoteTypes = () => {
    const { leadgenPopupType } = this.props

    if (leadgenPopupType) {
      return this.handleLeadgenPopupOpen(leadgenPopupType)
    }
    return this.handleGetMyQuote()
  }

  handleCallNow = () => {
    const { phoneMapping } = this.props
    const number = getPhoneNumber(phoneMapping)

    Pixels.callEvent()
    Analytics.log('Click To Call', {
      location: 'header', info: `tel:${ number }`
    })

    window.location.href = `tel:${ number }`
  }

  handleLeadgenPopupOpen = (flow: LeadgenPopupType) => {
    const variant = flow || 'MEDSUP'
    this.setState({ showLeadgenPopupFor: variant });

    Analytics.log('Showing Leadgen Popup', compileAnalyticsEventProps({ product: variant, variant }))
  }

  handleGetMyQuote = () => {
    this.setState({ showSelectProductPopup: true })
  }

  handleApptBannerSchedule = () => {
    this.handleLeadgenPopupOpen(LeadgenPopupType.SCHEDULE_AEP)
  }

  handleLearnMore = () => {
    setTimeout(() => {
      window.location.href = '/signup'
    }, 500)
  }

  handleLogin() {
    window.location.href = '/login';
  }

  handleRequestCall = () => {
    const { infoBlockActionType } = this.props;

    if (infoBlockActionType) {
      this.handleLeadgenPopupOpen(infoBlockActionType as LeadgenPopupType)
      return
    }

    this.setState({ showSelectProductPopup: true })
  }

  handleCloseGetMyQuote = () => {
    this.setState({ showSelectProductPopup: false })
  }

  handleLeadgenPopupClose = () => {
    this.setState({ showLeadgenPopupFor: false })
  }


  handleSelectProduct = async (product: string) => {
    const { address_zip } = this.props
    const {
      hasEnteredZipCode,
      hasEnteredState,
    } = this.state
    const flowType: LeadgenPopupType = product as LeadgenPopupType;

    const {
      medAdvQuotesFlag,
      medSupQuotesFlag,
      lifeQuotesFlag,
      pdpQuotesFlag
    } = await ABConfigsStore.getQuoteConfigs()

    const skipPopupFlow =
      (flowType === 'MEDSUP' && medSupQuotesFlag === 'pre_lead' && hasEnteredZipCode) ||
      (flowType === 'MEDADV' && medAdvQuotesFlag === 'pre_lead' && hasEnteredZipCode) ||
      (flowType === 'PDP' && pdpQuotesFlag === 'pre_lead' && hasEnteredZipCode) ||
      (flowType === 'LIFE_INSURANCE' && lifeQuotesFlag === 'pre_lead' && hasEnteredState)

    if (skipPopupFlow) {
      let nextUrl: string | null = null
      if (flowType === 'MEDADV' && medAdvQuotesFlag !== 'off') {
        nextUrl = await this.saveSunfireProspect({ address_zip })
      }
      if (flowType === 'PDP' && medAdvQuotesFlag !== 'off') {
        nextUrl = await this.saveSunfireProspect({ address_zip }, 'pdp')
      }

      this.handlePostSubmission(nextUrl, flowType)
    } else {
      this.handleLeadgenPopupOpen(flowType)
    }

    setTimeout(this.handleCloseGetMyQuote, 600)

    this.setState({
      hasEnteredState: false,
      hasEnteredZipCode: false,
    })
  }

  // Pre-fetchs the quotes so that we speed up the next real fetch on page transition.
  handleLeadgenPartialChange = (answers: Record<keyof StoreState, any>): void => {
    const { showLeadgenPopupFor } = this.state
    const {
      address_zip,
      dob_year,
      dob_month,
      dob_day,
      gender,
    } = {
      ...this.props,
      ...answers
    }
    const params = {
      gender: gender || 'male',
      tobacco: false,
      address_zip,
      dob: `${dob_month}/${dob_day}/${dob_year}`
    }

    if (
      showLeadgenPopupFor === 'MEDSUP' &&
      address_zip &&
      dob_year &&
      dob_month &&
      dob_day
    ) {
      const planName = getPlanByAge({
        dobYear: dob_year,
        dobMonth: dob_month,
        dobDay: dob_day,
      })

      if (planName) {
        ApiClient.getQuotes({
          ...params,
          plan: planName
        })
      }
      ApiClient.getQuotes(params)
    }
  }

  /*
   * Data Submission:
   */

  saveSunfireProspect = async (answers: Partial<StoreState>, product?: string): Promise<string | null> => {
    const {
      leadId,
      dob_day,
      dob_month,
      dob_year,
      gender,
      infoBlockActionType,
      mode,
    } = this.props
    const { showLeadgenPopupFor } = this.state
    const isMedAdvFlow = showLeadgenPopupFor === 'MEDADV' ||
      infoBlockActionType === 'MEDADV' ||
      !infoBlockActionType && mode === 'article'

    const { medAdvQuotesFlag, pdpQuotesFlag } = await ABConfigsStore.getQuoteConfigs()

    const isPdpFlow = showLeadgenPopupFor === 'PDP' ||
      infoBlockActionType === 'PDP'

    try {
      if (isMedAdvFlow && medAdvQuotesFlag !== 'off' || (isPdpFlow && pdpQuotesFlag !== 'off')) {
        const data = {
          ...answers,
          leadId,
          dob_day,
          dob_month,
          dob_year,
          gender,
          utm_source: Config.utm_source,
          utm_campaign: Config.utm_campaign,
          utm_content: Config.utm_content,
          utm_term: Config.utm_term,
          utm_medium: Config.utm_medium,
          utm_program: Config.utm_program,
          utm_call: Config.utm_call,
          fbclid: Config.fbclid,
          gclid: Config.gclid
        }
        const res = await saveIncomingProspects({ data, leadId, eventProps: compileAnalyticsEventProps(), product })
        return res
      } else {
        return null
      }
    } catch (e) {
      Log.warn(this, `Failed to track Sunfire prospects: ${e.message}`)
      return null
    }
  }

  public handlePostSubmission = (toUrl?: string | null, product?: LeadgenPopupType): boolean => {
    const { infoBlockActionType } = this.props
    const { showLeadgenPopupFor } = this.state

    const {
      medSupQuotesFlag,
      medAdvQuotesFlag,
      lifeQuotesFlag, pdpQuotesFlag
    } = ABConfigsStore.getQuoteConfigsSync()

    let nextUrl: string | undefined = undefined
    const productType = product || showLeadgenPopupFor

    switch (productType) {
      case 'MEDSUP': {
        if (medSupQuotesFlag !== 'off') {
          const skipBasicInfo = medSupQuotesFlag === 'pre_lead'
          nextUrl = toUrl || `/medicare-supplement/shop${!skipBasicInfo ? '?skipBasicInfo=true' : ''}`
        }
        break
      }
      case 'MEDADV': {
        if (medAdvQuotesFlag !== 'off') {
          nextUrl = toUrl || SUNFIRE_WEB_URL
        }
        break
      }
      case 'PDP': {
        if (pdpQuotesFlag !== 'off') {
          nextUrl = toUrl || SUNFIRE_WEB_URL
        }
        break
      }
      case 'LIFE_INSURANCE': {
        if (lifeQuotesFlag !== 'off') {
          const skipBasicInfo = lifeQuotesFlag === 'pre_lead'
          nextUrl = toUrl || `/life-insurance/shop${!skipBasicInfo ? '?skipBasicInfo=true' : ''}`
        }
        break
      }
      case false: {
        if (infoBlockActionType === 'MEDSUP' && medSupQuotesFlag !== 'off') {
          const skipBasicInfo = medSupQuotesFlag === 'pre_lead'
          nextUrl = toUrl || `/medicare-supplement/shop${!skipBasicInfo ? '?skipBasicInfo=true' : ''}`
        } else if (infoBlockActionType === 'MEDADV' && medAdvQuotesFlag !== 'off') {
          nextUrl = toUrl || SUNFIRE_WEB_URL
        }
        break
      }
    }

    nextUrl && setTimeout(() => {
      window.location.href = (nextUrl as string)
    }, 600) // wait for popup to close.

    return !nextUrl
  }

  handleLeadgenSubmit = (answers: Record<keyof StoreState, any>, product?: string): Promise<boolean> => {
    Analytics.log('Click', compileAnalyticsEventProps({ location: 'leadgen-popup', product }))

    const { onSaveSessionData } = this.props

    const keys = Object.keys(answers)
    keys.forEach(key => {
      onSaveSessionData(key, answers[key])
    })

    const promise = this.submitLead(answers)
    return promise
  }

  submitLead = async (answers: Record<string, any>): Promise<boolean> => {
    const { email, onSaveSessionData } = this.props
    const { showLeadgenPopupFor } = this.state

    const { medAdvQuotesFlag } = ABConfigsStore.getQuoteConfigsSync()

    Log.info(this, 'createLead')

    // if (this.props.leadId) {
    //   Log.info(this, 'lead already created: ' + leadId)
    //   Analytics.setLeadId(leadId)
    //   const shouldContinue = this.handlePostSubmission()
    //   return Promise.resolve(shouldContinue)
    // }

    // Skip the lead submit if the phone number or email are not provided.
    if (!answers.phone_number || !answers.email) {
      let nextUrl: string | null = null
      if (showLeadgenPopupFor === 'MEDADV' && medAdvQuotesFlag !== 'off') {
        nextUrl = await this.saveSunfireProspect(answers)
      }
      const shouldContinue = this.handlePostSubmission(nextUrl)
      return Promise.resolve(shouldContinue)
    }

    const leadData: LeadData = {
      ...this.props,
      ...answers,
      product_type: getLeadProductType(showLeadgenPopupFor || null),
      industry_type: getIndustryType(showLeadgenPopupFor || null),
      external_ab: getExternalAb(showLeadgenPopupFor || null),
    }

    if (medAdvQuotesFlag === 'post_lead' && showLeadgenPopupFor === LeadgenPopupType.MEDADV) {
      leadData.delay_call = 'sunfire_self_enroll'
    }

    if (showLeadgenPopupFor === LeadgenPopupType.SCHEDULE_AEP) {
      const { best_time_to_call, consultation_date } = leadData
      if (consultation_date && best_time_to_call) {
        const scheduledDate = new Date(consultation_date)
        scheduledDate.setHours(best_time_to_call === 'afternoon/evening' ? 15 : 9)
        // @ts-ignore
        leadData.next_followup_time = scheduledDate.getTime() / 1000
        Analytics.log('Scheduled Call', compileAnalyticsEventProps({
          location: 'leadgen-popup',
          external_ab: leadData.external_ab,
          product_type: leadData.product_type,
        }))
      }
    }

    const result = await ApiClient.submitLead(leadData)

    if (result && result.error) {
      Analytics.log('Form Error', compileAnalyticsEventProps({
        location: 'contact',
        info: `external error on ${result.key}: ${result.message}`
      }))
      throw new Error('Something went wrong, please try again.')
    }

    const leadId = result.lid
    const userId = result.uid
    const quizVersion = result.quiz_version
    Config.quiz_version = quizVersion

    Pixels.lead(leadId, email)

    Analytics.log('Leadgen Next', compileAnalyticsEventProps({
      uid: userId,
      lid: leadId,
      industry_type: leadData.industry_type,
      product_type: leadData.product_type,
      product: showLeadgenPopupFor
    }))

    Analytics.setLeadId(leadId)

    Log.info(this, `lead id: ${leadId}`)
    Log.info(this, `user id: ${userId}`)
    Log.info(this, `quiz version: ${quizVersion}`)

    onSaveSessionData('leadId', leadId)
    onSaveSessionData('quizVersion', quizVersion)

    if (userId) {
      Analytics.setUserId(userId)
      onSaveSessionData('userId', userId)
    }

    const keys = Object.keys(answers)
    keys.forEach(key => {
      onSaveSessionData(key, answers[key])
    })

    const nextUrl = await this.saveSunfireProspect(answers)

    const shouldContinue = this.handlePostSubmission(nextUrl)

    return shouldContinue
  }


  removeQuestionFromForm(formData: LeadgenFormData, key: string): LeadgenFormData {
    formData.questions = formData.questions.filter(q => q.key !== key);
    return formData;
  }

  /*
   * Rendering:
   */

  renderLeadgenFlow() {
    const {
      showLeadgenPopupFor,
      hasEnteredZipCode,
    } = this.state

    const {
      medAdvQuotesFlag,
      medSupQuotesFlag,
      lifeQuotesFlag,
    } = ABConfigsStore.getQuoteConfigsSync()

    if (!showLeadgenPopupFor) return null

    switch (showLeadgenPopupFor) {
      case 'MEDADV': {
        if (medAdvQuotesFlag === 'pre_lead' &&!hasEnteredZipCode) {
          return this.renderPreLeadPopup()
        }
        break
      }
      case 'MEDSUP': {
        if (medSupQuotesFlag === 'pre_lead' && !hasEnteredZipCode) {
          return this.renderPreLeadPopup()
        }
        break
      }
      case 'LIFE_INSURANCE': {
        if (lifeQuotesFlag === 'pre_lead') {
          return this.renderPreLeadPopup()
        }
        break
      }
    }

    return showLeadgenPopupFor
      ? this.renderLeadgenPopup()
      : null
  }

  renderPreLeadPopup() {
    const { showLeadgenPopupFor } = this.state
    const { address_zip, address_state } = this.props

    return showLeadgenPopupFor && (
      <PreLeadPopup
        variant={showLeadgenPopupFor}
        onClose={this.handleLeadgenPopupClose}
        onSubmit={this.handleLeadgenSubmit}
        address_zip={address_zip}
        address_state={address_state}
      />
    )
  }

  renderLeadgenPopup() {
    const { consultation_date, best_time_to_call } = this.props
    const { showLeadgenPopupFor } = this.state

    const {
      medAdvQuotesFlag,
      medSupQuotesFlag
    } = ABConfigsStore.getQuoteConfigsSync()

    let formData: LeadgenFormData = MSLeadgenData as LeadgenFormData
    if(showLeadgenPopupFor === LeadgenPopupType.MEDSUP && medSupQuotesFlag === 'off'){
      formData = this.removeQuestionFromForm(formData, 'gender');
    }

    switch (showLeadgenPopupFor) {
      case LeadgenPopupType.MEDADV: {
        formData = MALeadgenData as LeadgenFormData;
        break
      }
      case LeadgenPopupType.LIFE_INSURANCE: {
        formData = LILeadgenData as LeadgenFormData
        break
      }
      case LeadgenPopupType.AUTO_INSURANCE: {
        formData = AILeadgenData as LeadgenFormData
        break
      }
      case LeadgenPopupType.DISABILITY: {
        formData = DILeadgenData as LeadgenFormData
        break
      }
      case LeadgenPopupType.GUARANTEED_ISSUE: {
        formData = GILeadgenData as LeadgenFormData
        break
      }
      case LeadgenPopupType.SCHEDULE_AEP: {
        formData = ScheduleLeadgenData as LeadgenFormData;
        break;
      }
    }

    return showLeadgenPopupFor && (
      <LeadgenPopupFlow
        flow={showLeadgenPopupFor}
        answersPreset={this.props as StoreState}
        formData={formData}
        onClose={this.handleLeadgenPopupClose}
        onSubmit={this.handleLeadgenSubmit}
        onPartialChange={this.handleLeadgenPartialChange}
        forceOptionalFields={!!medAdvQuotesFlag && showLeadgenPopupFor === 'MEDADV'}
        industryType={getIndustryType(showLeadgenPopupFor)}
        consultationDate={consultation_date}
        bestTimeToCall={best_time_to_call}
      />
    )
  }

  render() {
    const {
      headerStyles,
      hideLogoTagline,
      navItems,
      phoneMapping,
      headerText,
      headerButtonText,
      showBannerType,
      showMainGreenButton,
    } = this.props
    const { showSelectProductPopup } = this.state

    return (
      <Fragment>
        <PublicHeader
          greenBtnText={headerButtonText || 'Get my quote'}
          navItems={navItems || headerNavData as NavGroupType[]}
          onGreenBtnClick={showMainGreenButton ? this.handleGreenButtonClick : undefined}
          onGetMyQuote={showMainGreenButton ? this.handleGetMyQuoteTypes : undefined}
          onLogin={this.handleLogin}
          onRequestCall={this.handleRequestCall}
          phoneMapping={phoneMapping}
          hideLogoTagline={hideLogoTagline}
          onScheduleAEP={this.handleApptBannerSchedule}
          onLearnMore={this.handleLearnMore}
          showBannerType={showBannerType}
          css={headerStyles}
          headerText={headerText}
        />

        {showSelectProductPopup && (
          <ProductSelectPopup
            onClose={this.handleCloseGetMyQuote}
            products={productsData}
            onSelectProduct={this.handleSelectProduct}
            children={null}
          />
        )}

        {this.renderLeadgenFlow()}
      </Fragment>
    )
  }
}
